import React, {useMemo} from 'react'
import {ProgressBar} from "react-step-progress-bar";
import {useNavigate, Link, Outlet, useParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import SupplierTalentWelcome from "./SupplierTalentWelcome";
import ResumeImportSelection from "../talent-application/components/ResumeImportSelection";
import { Briefcase, Building2,Scroll, History, Award, GraduationCap } from 'lucide-react';
import AiIcon from "../../components/AiIcon";
import {Divider} from "antd";
import SpinLoader from "../../components/SpinLoader";

const ProfileApprovalIntro = (props) => {
    const {auth} = useAuth();

    return (
        <div className="welcome-panel">
            <div className="welcome-top text-center">
                <h1>Hello {auth?.first_name}</h1>
                <h1>Welcome to Wripple!</h1>
                <p className="welcome-text">Congratulations on being an approved talent member.</p>
                <img src="/images/talent-welcome/teamwork.png" className="w-full max-w-[450px] mx-auto -mt-6"/>
            </div>

            <div className="welcome-bottom pb-5 md:flex">
                <div className="w-full text-center md:w-1/2">
                    <p className="m-0 welcome-text">Now let's get to work.</p>
                </div>
                <div className="mt-4 w-full flex justify-center md:justify-end md:w-1/2 md:mt-0">
                    <Link className="btn btn-primary" to={auth.isProfileApproved() ? `/talent/welcome/platform-highlights` : `/talent/welcome/next-steps`}>
                        Next
                    </Link>
                </div>
            </div>
        </div>
    )
}
const ProfileApprovalPlatformHighlights = (props) => {
    const {auth} = useAuth();

    const cards = [
        {title: 'Manage your\nprofile', img_src: 'profile'},
        {title: 'Let us know\nyour availability', img_src: 'calendar'},
        {title: 'Browse our\nJob Board', img_src: 'job-board'},
        {title: 'Pursue freelance\nprojects', img_src: 'freelance'},
        {title: 'Track payments,\ncontracts & invoices', img_src: 'payments'},
        {title: 'Refer your friends', img_src: 'friends', sub_title: '(earn extra cash)'},
    ]
    return (
        <div className="welcome-panel platform-features-panel">

            <div className="welcome-top text-center">
                <h1 className="welcome-text">Remember Wripple is Entirely Free to Use. Below are highlights of
                    current platform features, and many more are on the way in upcoming releases.</h1>

                <div className="welcome-icons-container">

                    {cards.map(card => {
                        return (
                            <div className="w-1/2 md:w-1/3" key={card.img_src}>
                                <div className="welcome-icon-card">
                                    <img alt={card.title} src={`/images/talent-welcome/${card.img_src}.png`}/>
                                    <p>{card.title}</p>
                                    {card.sub_title && (
                                        <p className="icon-card-subtitle">{card.sub_title}</p>
                                    )}
                                </div>

                            </div>
                        )
                    })}

                </div>
            </div>

            <div className="welcome-bottom">
                <div className="w-full flex justify-center lg:justify-end">
                    <Link className="btn btn-primary" to="/talent/welcome/next-steps">
                        Next
                    </Link>
                </div>
            </div>
        </div>
    )
}

const ProfileApprovalResumeImport = (props) => {
    const {auth, isUpdating, talentUpdate, markTalentWelcomeAsViewed} = useAuth();
    const navigate = useNavigate();

    const continueWithProfile = async () => {
        await markTalentWelcomeAsViewed();
        return navigate({
            pathname: "/talent/application"
        }, {replace: true})
    }
    const persistImport = async (params) => {
        let didImport = await talentUpdate(params)

        if (didImport) {
            TB.toast('Profile Updated!');
            return continueWithProfile()
        }else TB.showServerErrorToast();
    }

    return (
        <SpinLoader isLoading={isUpdating} className="welcome-panel platform-features-panel">

            <div className="welcome-top text-center">

                <h1 className="welcome-text">Jump start your profile by auto-filling from your resume.&nbsp;</h1>

                <p className="text-base mb-3">
                    Check which items your would like to autofill your profile. Note: You can edit these items later after importing.
                </p>

                <ResumeImportSelection onSubmit={persistImport} isDisabled={props.disabled}>
                    <button className="btn btn-dark md:w-3/12"
                            onClick={continueWithProfile}>
                        Skip, Continue to Profile
                    </button>
                </ResumeImportSelection>

            </div>

        </SpinLoader>
    )
}


const ProfileItems = () => {
    const items = [
        {
            title: 'Project Highlights',
            icon: <Briefcase className="w-8 h-8 text-white"/>,
            description: 'Showcase of key projects and their measurable impact',
            bgColor: TB.config('color.info')
        },
        {
            title: 'Brands',
            icon: <Building2 className="w-8 h-8 text-white"/>,
            description: 'Top companies and clients worked with',
            bgColor: TB.config('color.secondary')
        },
        {
            title: 'Work History',
            icon: <History className="w-8 h-8 text-white" />,
            description: 'Career timeline and professional growth',
            bgColor: TB.config('color.success')
        },
        {
            title: 'Education',
            icon: <GraduationCap className="w-6 h-6 text-white" />,
            description: 'Academic background and qualifications.',
            bgColor: TB.config('color.black')
        },
        {
            title: 'Awards',
            icon: <Award className="w-8 h-8 text-white" />,
            description: 'Industry recognition and achievements',
            bgColor: TB.config('color.warning')
        },
        {
            title: 'Certifications',
            icon: <Scroll className="w-8 h-8 text-white" />,
            description: 'Professional qualifications and expertise',
            bgColor: TB.config('color.danger')
        }
    ];

    return (
        <div className="flex flex-wrap justify-evenly gap-6">
            {items.map((item, index) => (
                <div
                    key={index}
                    className="start-tile py-6"
                >
                    <div className="p-4 mb-3 shadow-sm"
                        style={TB.createStyledCircle(item.bgColor, 1)}>
                        {item.icon}
                    </div>
                    <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-600">{item.description}</p>
                </div>
            ))}
        </div>
    );
};
const ProfileApprovalNextSteps = (props) => {
    const {auth, markTalentWelcomeAsViewed} = useAuth();
    const progress = useMemo(() => auth?.profileProgress(), [auth]);
    const navigate = useNavigate();

    const canImportFromResume = useMemo(() => auth?.canImportResumeItemsForProfile(), [auth])

    return (
        <div className="welcome-panel px-12 pb-12">

            <div className="welcome-top">
                <p className="welcome-text bold">Winning work starts with a great profile</p>

                <div className="panel-progress mt-3">
                    <p className="progress-title m-0">{`${progress}% of profile completed`}</p>
                    <ProgressBar
                        filledBackground={TB.config('color.light_green')}
                        height={20}
                        percent={progress}
                        unfilledBackground={TB.config('color.gray')}
                    />
                </div>
            </div>
            <div className="flex flex-col items-start self-center gap-1 px-10">
                <p className="font-medium text-base mt-4">
                    We want to help showcase your professional journey effectively. The sections below will help
                    create a comprehensive view of your expertise.
                </p>
            </div>
            <div className="talent-start py-3">
                <div className="start-tile-container container mx-auto px-14 sm:px-2">
                    <ProfileItems/>
                </div>
            </div>
            <div className="flex flex-col mb-10 items-start self-center gap-1 text-base font-medium px-10">
                <p>
                    To get your profile approved, complete each section shown above. Once completed, you can request
                    your profile to be reviewed for approval. Our team is here to assist you at any step of the process.
                </p>
                {canImportFromResume && (
                    <React.Fragment>
                        <Divider/>
                        <p>
                            Want a quick start? You can save time by importing content directly from your resume using
                            our
                            AI tools.
                        </p>
                    </React.Fragment>
                )}
            </div>
            <div className="flex items-center justify-center gap-4">
                {canImportFromResume && (
                    <Link className="btn btn-primary"
                          to="/talent/welcome/import-resume">
                        <AiIcon fill="#ffffff"/>
                        <span className="ml-1">
                            Import from Resume
                        </span>
                    </Link>
                )}
                <button className="btn btn-outline-dark" onClick={async () => {
                    await markTalentWelcomeAsViewed();
                    return navigate({
                        pathname: "/talent/application"
                    }, {replace: true})
                }}>
                    Let's Start
                </button>
            </div>
        </div>
    )
}

const TalentProfileApprovedDetails = (props) => {

    const {auth, markTalentWelcomeAsViewed} = useAuth();
    const progress = useMemo(() => auth?.profileProgress(), [auth]);
    const navigate = useNavigate();

    return (
        <div className="welcome-panel">

            <div className="welcome-top">
                <p className="welcome-text bold">Winning work starts with a great profile</p>

                <div className="panel-progress mt-3">
                    <p className="progress-title m-0">{`${progress}% of setup complete`}</p>
                    <ProgressBar
                        filledBackground={TB.config('color.light_green')}
                        height={20}
                        percent={progress}
                        unfilledBackground={TB.config('color.gray')}
                    />
                </div>
            </div>

            <div className="winning-work-actions">
                <div className="welcome-actions-section">
                    <img src="/images/talent-welcome/teamwork-writing.png" className="img-fluid block mx-auto"/>

                    <button
                        className="btn btn-primary"
                        onClick={async () => {
                            await markTalentWelcomeAsViewed();
                            return navigate({
                                pathname: "/talent/application/bio?importResume=1",
                                // search: '?uploadResume=true'
                            }, {replace: true})
                        }}
                    >
                        Update Profile Now
                    </button>
                </div>

                <div className="divider"/>

                <div className="welcome-actions-section">
                    <img src="/images/talent-welcome/dashboard-preview.png" className="img-fluid block mx-auto"/>

                    <button className="btn btn-primary"
                            onClick={async () => {
                                await markTalentWelcomeAsViewed();
                                return navigate(appropriate(), {replace: true})
                            }}>
                        Skip to My Dashboard
                    </button>
                    <p className="actions-hint">I'll make profile updates later</p>
                </div>
            </div>

        </div>
    )
}

const TalentProfileApprovalWalkthrough = (props) => {

    const {auth, markTalentWelcomeAsViewed, shouldSeeTalentWelcome, appropriate} = useAuth();

    const {"*": params} = useParams();

    if (auth.isTalentSupplierResource()) {
        return <SupplierTalentWelcome/>
    }

    return (
        <div className={'talent-portal-page'}>
            <div className="nav-container">
                <div className="nav-info">

                    <div className="nav-welcome">
                        <p>Welcome</p>
                    </div>
                </div>
            </div>
            <div className="talent-portal-welcome">
                <div className="container mx-auto">
                    <Outlet/>
                </div>
            </div>
        </div>
    )
}

export default TalentProfileApprovalWalkthrough;

export {
    ProfileApprovalIntro,
    ProfileApprovalPlatformHighlights,
    ProfileApprovalResumeImport,
    ProfileApprovalNextSteps,
    TalentProfileApprovedDetails
}